import {
  type NexiRetrievePaymentResponse,
  type Option,
  type OrderDTO,
  type PaymentProviderConfig,
  type ShippingProviderConfig
} from 'ecosystem'
import { getStorefrontOrderByOrderNumber } from '../getStorefrontOrderByOrderNumber'
import { nexiApi } from '../../../utils/nexi'
import { createError } from '../../../utils/fetching'
import { alertService } from '../../../utils/alert-service'
import { completeStorefrontOrder } from './completeStorfrontOrder'
import type { OrderProvisionResult, ProvisionOrderOptions } from './types'
import {
  generateIngridShippingProviderResult,
  generateNexiAddresses,
  generateNshiftShippingProviderResult,
  generatePostnordShippingProviderResult,
  isOrderAlreadyProvisioned,
  isOrderFresh
} from './utils'

export interface ProvisionOrderNexiProviderResult {
  nexiResponse: NexiRetrievePaymentResponse
  storefrontOrder: Option<OrderDTO>
  shippingProviderResult?: OrderProvisionResult['shippingProviderResult']
}

export const provisionOrderNexiProvider = async (
  paymentId: string,
  paymentProviderConfig?: PaymentProviderConfig,
  shippingProviderConfig?: ShippingProviderConfig,
  options?: ProvisionOrderOptions
): Promise<Option<ProvisionOrderNexiProviderResult>> => {
  // 1. Confirmation in Nexi
  const { error: nexiError, data: nexiPaymentResponse } =
    await nexiApi.checkout.retrievePayment(paymentId)

  if (nexiError || !nexiPaymentResponse) {
    void alertService.send('Provision order | Get nexi payment', {
      blocks: [
        {
          title: 'Payload',
          content: {
            paymentId
          }
        },
        {
          title: 'Error',
          content: nexiError
        }
      ]
    })

    throw Error(nexiError || 'Nexi GET endpoint error')
  }

  // 2. Checks if the order exists
  const { error: getOrderError, data: orderData } = await getStorefrontOrderByOrderNumber({
    orderNumber: nexiPaymentResponse.payment.orderDetails.reference
  })

  // 3. If the order doesn't exist, returns an error
  if (getOrderError || !orderData) {
    void alertService.send('Provision order | Get storefront order', {
      blocks: [
        {
          title: 'Payload',
          content: {
            orderNumber: nexiPaymentResponse.payment.orderDetails.reference
          }
        },
        {
          title: 'Nexi Payment Response',
          content: nexiPaymentResponse
        },
        {
          title: 'Error',
          content: getOrderError
        }
      ]
    })

    throw createError(getOrderError ?? 'BE GET endpoint error')
  }

  // Idempotency guard: the order is provisioned exactly once. If it is no longer CREATED, another
  // path (confirmation page, or an earlier push retry) already provisioned it — return the existing
  // order without re-booking shipment or re-writing it, to avoid duplicates.
  if (isOrderAlreadyProvisioned(orderData)) {
    return { storefrontOrder: orderData, nexiResponse: nexiPaymentResponse }
  }

  // Fallback gate (payment push only): a still-fresh order belongs to the confirmation page; throw
  // so the push endpoint responds non-2xx and the provider retries later. This keeps the page the
  // single provisioner during the post-checkout window, eliminating the page-vs-push race.
  if (options?.asFallback && isOrderFresh(orderData)) {
    throw createError(
      'Payment push received for a still-fresh order; deferring to the confirmation page'
    )
  }

  const getShippingProviderResult = async (): Promise<
    OrderProvisionResult['shippingProviderResult'] | void
  > => {
    if (!orderData?.shippingProvider) {
      return
    }

    switch (orderData?.shippingProvider) {
      case 'INGRID':
        return generateIngridShippingProviderResult(orderData)
      case 'POSTNORD': {
        const { customerInfo } = generateNexiAddresses(nexiPaymentResponse)

        return generatePostnordShippingProviderResult(
          orderData,
          {
            phoneNo: customerInfo.phone,
            smsNo: customerInfo.phone,
            emailAddress: customerInfo.email,
            contactName: customerInfo.fullName
          },
          shippingProviderConfig
        )
      }
      case 'NSHIFT': {
        const { customerInfo } = generateNexiAddresses(nexiPaymentResponse)

        return generateNshiftShippingProviderResult(orderData, {
          phone: customerInfo.phone,
          email: customerInfo.email,
          name: customerInfo.fullName
        })
      }
      case 'DEFAULT':
    }
  }

  // 4. Get shipping provider information
  const shippingProviderResult: OrderProvisionResult['shippingProviderResult'] | void =
    await getShippingProviderResult()

  // 5. Complete storefront order, change status to PAID and update all info
  const storefrontOrder = await completeStorefrontOrder({
    order: orderData,
    paymentProviderInfo: {
      ...paymentProviderConfig,
      providerName: 'NEXI',
      providerResponse: {
        nexiResponse: nexiPaymentResponse
      }
    },
    ...(shippingProviderResult && {
      shippingProviderInfo: shippingProviderResult
    })
  })

  return {
    storefrontOrder,
    nexiResponse: nexiPaymentResponse,
    ...(shippingProviderResult && {
      shippingProviderResult
    })
  }
}
